import React, { useEffect, useState } from "react";
import { ChevronsUpDown, PanelRightClose } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@exiftrator/shared/components/ui";
import PreviewTab from "../ContextPanel/PreviewTab";
import MapTab from "../ContextPanel/MapTab";
import AssistantDiffTab from "./AssistantDiffTab";
import { HeaderIconButton } from "../Header/HeaderActions";
import { PanelHeader } from "../PanelHeader";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import { useStorageService } from "../../contexts/PlatformContext";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";
import { cn } from "../utils/cn";

const STORAGE_KEY = "editor.assistant.sections.v1";
const DEFAULT_SECTIONS = {
  preview: true,
  map: true,
  diff: true,
};

function normalizeSections(value) {
  if (!value || typeof value !== "object") {
    return DEFAULT_SECTIONS;
  }

  return {
    preview:
      typeof value.preview === "boolean" ? value.preview : DEFAULT_SECTIONS.preview,
    map: typeof value.map === "boolean" ? value.map : DEFAULT_SECTIONS.map,
    diff: typeof value.diff === "boolean" ? value.diff : DEFAULT_SECTIONS.diff,
  };
}

function AssistantSection({ title, open, onOpenChange, children }) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center justify-between gap-2 py-2">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex size-6 items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
          >
            <ChevronsUpDown className="size-4" />
            <span className="sr-only">{open ? `Collapse ${title}` : `Expand ${title}`}</span>
          </button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="pb-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

function AssistantPane({ onHideDesktopRight = () => {} }) {
  const storage = useStorageService();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const paneInsetXClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.paneInsetX,
  );
  const paneInsetYClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.paneInsetY,
  );
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadSections = async () => {
      try {
        const stored = await storage.getItem(STORAGE_KEY);
        if (!stored) return;
        const parsed = JSON.parse(stored);
        if (!cancelled) {
          setSections(normalizeSections(parsed));
        }
      } catch (error) {
        console.warn("Failed to load assistant section settings", error);
      } finally {
        if (!cancelled) {
          setHasLoadedSettings(true);
        }
      }
    };

    loadSections();
    return () => {
      cancelled = true;
    };
  }, [storage]);

  useEffect(() => {
    if (!hasLoadedSettings) return;

    storage
      .setItem(STORAGE_KEY, JSON.stringify(sections))
      .catch((error) => console.warn("Failed to save assistant section settings", error));
  }, [storage, sections, hasLoadedSettings]);

  return (
    <aside className="flex h-full min-h-0 w-[320px] flex-col border-l border-border-default bg-bg-secondary">
      <PanelHeader
        title="Assistant"
        rightContent={
          !isMobile ? (
            <HeaderIconButton
              intent="utility"
              aria-label="Hide assistant panel"
              title="Hide assistant panel"
              onClick={onHideDesktopRight}
            >
              <PanelRightClose className="size-4" />
            </HeaderIconButton>
          ) : null
        }
      />

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          paneInsetXClass,
          paneInsetYClass,
        )}
      >
        <div className={WORKSPACE_SPACING.sectionGapPrimary}>
          <AssistantSection
            title="Preview"
            open={sections.preview}
            onOpenChange={(open) => setSections((prev) => ({ ...prev, preview: open }))}
          >
            <PreviewTab />
          </AssistantSection>

          <AssistantSection
            title="Map"
            open={sections.map}
            onOpenChange={(open) => setSections((prev) => ({ ...prev, map: open }))}
          >
            <MapTab />
          </AssistantSection>

          <AssistantSection
            title="Diff"
            open={sections.diff}
            onOpenChange={(open) => setSections((prev) => ({ ...prev, diff: open }))}
          >
            <AssistantDiffTab />
          </AssistantSection>
        </div>
      </div>
    </aside>
  );
}

export default AssistantPane;
